<template>
    <div class="limiter-content pa-3 pa-lg-0">
        <div class="padding-block"></div>

        <v-row no-gutters justify="center" class="mb-5">
            <v-col lg="12">
                <page-title eyebrow="ÁREA DO PROCURADOR" icon="mdi-briefcase-account-outline" title="Cadastro" highlight="do credor"
                    subtitle="Endereço, representante legal e documentos do credor representado, exigidos na adesão ao plano." />

                <router-link to="/procurador/credores" class="back-link mb-5">
                    <v-icon small color="primary">mdi-chevron-left</v-icon>
                    <span>Voltar para Meus credores</span>
                </router-link>

                <login-required-notice v-if="!$root.user" />
                <v-progress-linear v-else-if="loading" indeterminate></v-progress-linear>
                <v-alert v-else-if="error" type="error" text>{{ error }}</v-alert>

                <template v-else-if="data">
                    <v-card class="card-form-outlined elevation-1 pa-5 mb-6">
                        <div class="text-h6 font-weight-bold text-capitalize">{{ data.creditor.name }}</div>
                        <div class="grey--text text--darken-1">{{ isPj ? "CNPJ" : "CPF" }} {{ formatRegistration(data.creditor.registration) }}</div>
                        <v-alert v-if="!data.completeness.missing.length" type="success" outlined dense class="mt-4 mb-0">
                            Cadastro completo para a adesão ao plano.
                        </v-alert>
                        <v-alert v-else type="warning" outlined dense class="mt-4 mb-0">
                            Pendências para a adesão ao plano: {{ data.completeness.missing.map((item) => item.label).join(", ") }}.
                        </v-alert>
                    </v-card>

                    <v-card class="card-form-outlined elevation-1 pa-5">
                        <v-form ref="form" v-model="validForm">
                            <v-card-title class="card-group-title px-0">Endereço do credor</v-card-title>
                            <v-card-text class="px-0">
                                <v-row>
                                    <v-col cols="12" sm="3">
                                        <v-text-field dense label="CEP" v-model="form.zip_code" v-mask="'#####-###'"
                                            :rules="[rules.zipCode]" outlined hide-details="auto"></v-text-field>
                                    </v-col>
                                    <v-col cols="12" sm="7">
                                        <v-text-field dense label="Logradouro" v-model="form.street" maxlength="255"
                                            outlined hide-details="auto"></v-text-field>
                                    </v-col>
                                    <v-col cols="12" sm="2">
                                        <v-text-field dense label="Número" v-model="form.address_number" maxlength="20"
                                            outlined hide-details="auto"></v-text-field>
                                    </v-col>
                                </v-row>
                                <v-row>
                                    <v-col cols="12" sm="4">
                                        <v-text-field dense label="Complemento" v-model="form.address_complement" maxlength="120"
                                            outlined hide-details="auto"></v-text-field>
                                    </v-col>
                                    <v-col cols="12" sm="3">
                                        <v-text-field dense label="Bairro" v-model="form.district" maxlength="120"
                                            outlined hide-details="auto"></v-text-field>
                                    </v-col>
                                    <v-col cols="12" sm="3">
                                        <v-text-field dense label="Cidade" v-model="form.city" maxlength="120"
                                            outlined hide-details="auto"></v-text-field>
                                    </v-col>
                                    <v-col cols="12" sm="2">
                                        <v-select dense label="UF" v-model="form.state" :items="ufs" clearable
                                            outlined hide-details="auto"></v-select>
                                    </v-col>
                                </v-row>
                            </v-card-text>

                            <template v-if="isPj">
                                <v-card-title class="card-group-title px-0">Representante legal da empresa</v-card-title>
                                <v-card-text class="px-0">
                                    <v-row>
                                        <v-col cols="12" sm="5">
                                            <v-text-field dense label="Nome completo" v-model="form.legal_rep_name" maxlength="255"
                                                outlined hide-details="auto"></v-text-field>
                                        </v-col>
                                        <v-col cols="12" sm="3">
                                            <v-text-field dense label="CPF" v-model="form.legal_rep_registration" v-mask="'###.###.###-##'"
                                                :rules="[rules.cpf]" outlined hide-details="auto"></v-text-field>
                                        </v-col>
                                        <v-col cols="12" sm="4">
                                            <v-text-field dense label="Cargo" v-model="form.legal_rep_role" placeholder="Ex.: Sócio-administrador" maxlength="120"
                                                outlined hide-details="auto"></v-text-field>
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                            </template>

                            <v-card-actions class="px-0 mt-2">
                                <v-btn color="primary" elevation="0" :loading="saving" @click="save">Salvar</v-btn>
                            </v-card-actions>
                        </v-form>
                    </v-card>

                    <v-card v-if="isPj" class="card-form-outlined elevation-1 pa-5 mt-8">
                        <v-card-title class="card-group-title px-0">Documentos societários</v-card-title>
                        <v-card-text class="px-0">
                            <p class="mb-4">
                                Necessários para aderir ao plano em nome da empresa. Cada documento é analisado pelo Administrador Judicial,
                                e a adesão só é liberada com os três aprovados. Formatos aceitos: PDF ou imagem, até 10 MB.
                            </p>
                            <v-row v-for="docType in companyDocTypes" :key="docType.id" align="center" class="mb-2">
                                <v-col cols="12" sm="5" class="font-weight-bold">
                                    <v-icon :color="docState(docType.id).color" class="mr-2">{{ docState(docType.id).icon }}</v-icon>
                                    {{ docType.name }}
                                </v-col>
                                <v-col cols="12" sm="4">
                                    <template v-if="docOf(docType.id)">
                                        <div class="text-caption">
                                            Enviado: <a v-if="docOf(docType.id).url" :href="docOf(docType.id).url" target="_blank" rel="noopener">{{ docOf(docType.id).name }}</a>
                                            <span v-else>{{ docOf(docType.id).name }}</span>
                                        </div>
                                        <div class="text-caption font-weight-bold" :class="`${docState(docType.id).color}--text`">{{ docState(docType.id).label }}</div>
                                        <div v-if="docState(docType.id).reason" class="text-caption error--text">Motivo: {{ docState(docType.id).reason }}</div>
                                    </template>
                                    <span v-else class="text-caption grey--text">Não enviado</span>
                                </v-col>
                                <v-col cols="12" sm="3" class="text-sm-right">
                                    <v-btn small outlined color="primary" :loading="uploadingType === docType.id" @click="pickDoc(docType.id)">
                                        {{ docOf(docType.id) ? "Substituir" : "Enviar" }}
                                    </v-btn>
                                </v-col>
                            </v-row>
                            <input ref="docInput" type="file" accept=".pdf,.png,.jpg,.jpeg" class="d-none" @change="uploadDoc">
                        </v-card-text>
                    </v-card>
                </template>
            </v-col>
        </v-row>
    </div>
</template>

<script>
import axios from "axios";
import { cpf, cnpj } from "cpf-cnpj-validator";
import { errorMessage } from "@/components/plan-adhesion/plan-adhesion.util";

const UFS = ["AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"];
// Tipos de user_docs do cadastro PJ (UserDocTypeEnum no backend).
const COMPANY_DOC_TYPES = [
    { id: 11, name: "Contrato/Estatuto social" },
    { id: 12, name: "Ata ou documento de eleição do representante" },
    { id: 13, name: "Documento do representante legal" },
];
const FIELDS = ["zip_code", "street", "address_number", "address_complement", "district", "city", "state", "legal_rep_name", "legal_rep_registration", "legal_rep_role"];
const onlyDigits = (value) => String(value || "").replace(/\D/g, "");

// Cadastro complementar do credor representado, exigido na adesao ao plano.
// O procurador tem o mesmo poder do credor em "Minha conta".
export default {
    name: "ProcuratorCreditorProfile",

    data: () => ({
        loading: false,
        error: null,
        data: null,
        form: {},
        validForm: true,
        saving: false,
        uploadingType: null,
        pendingType: null,
        ufs: UFS,
        companyDocTypes: COMPANY_DOC_TYPES,
        rules: {
            zipCode: (value) => !value || onlyDigits(value).length === 8 || "CEP deve ter 8 dígitos",
            cpf: (value) => !value || cpf.isValid(onlyDigits(value)) || "CPF inválido",
        },
    }),

    computed: {
        isPj() {
            return !!this.data && this.data.creditor.person_type === "pj";
        },
    },

    created() {
        if (this.$root.user) this.load();
    },

    methods: {
        notifyError(error) {
            this.$dialog.notify.error(errorMessage(error), { position: "top-right", timeout: 5000 });
        },

        apply(content) {
            this.data = content;
            this.form = Object.fromEntries(FIELDS.map((field) => [field, content.creditor[field] || ""]));
        },

        async load() {
            this.loading = true;
            this.error = null;
            try {
                const result = await axios.get(`/api/procurators/creditors/${this.$route.params.id}/profile`);
                this.apply(result.data.content);
            } catch (error) {
                this.error = errorMessage(error, "Não foi possível carregar o cadastro do credor.");
            } finally {
                this.loading = false;
            }
        },

        formatRegistration(value) {
            const digits = onlyDigits(value);
            return digits.length === 14 ? cnpj.format(digits) : digits.length === 11 ? cpf.format(digits) : value;
        },

        async save() {
            if (!this.$refs.form.validate()) return;
            this.saving = true;
            try {
                const payload = { ...this.form, zip_code: onlyDigits(this.form.zip_code), legal_rep_registration: onlyDigits(this.form.legal_rep_registration) };
                if (!this.isPj) ["legal_rep_name", "legal_rep_registration", "legal_rep_role"].forEach((field) => delete payload[field]);
                const result = await axios.patch(`/api/procurators/creditors/${this.$route.params.id}/profile`, payload);
                this.apply(result.data.content);
                this.$dialog.notify.success("Cadastro do credor salvo", { position: "top-right", timeout: 2000 });
            } catch (error) {
                this.notifyError(error);
            } finally {
                this.saving = false;
            }
        },

        docOf(typeId) {
            return (this.data.company_docs || []).find((doc) => Number(doc.type) === typeId) || null;
        },

        // Situacao na analise do AJ: 0 em analise, 1 aprovado, 2 recusado.
        docState(typeId) {
            const doc = this.docOf(typeId);
            if (!doc) return { icon: "mdi-alert-circle-outline", color: "grey", label: "" };
            if (Number(doc.status) === 1) return { icon: "mdi-check-circle", color: "success", label: "Aprovado" };
            if (Number(doc.status) === 2) return { icon: "mdi-close-circle", color: "error", label: "Recusado: envie novamente", reason: doc.review_reason };
            return { icon: "mdi-clock-outline", color: "warning", label: "Em análise pelo Administrador Judicial" };
        },

        pickDoc(typeId) {
            this.pendingType = typeId;
            this.$refs.docInput.value = "";
            this.$refs.docInput.click();
        },

        async uploadDoc(event) {
            const file = event.target.files && event.target.files[0];
            const typeId = this.pendingType;
            if (!file || !typeId) return;
            if (file.size > 10 * 1024 * 1024) {
                this.$dialog.notify.error("O arquivo deve ter no máximo 10 MB", { position: "top-right", timeout: 3000 });
                return;
            }
            this.uploadingType = typeId;
            try {
                const formData = new FormData();
                formData.append("type", String(typeId));
                formData.append("name", file.name);
                formData.append("file", file);
                const result = await axios.post(`/api/procurators/creditors/${this.$route.params.id}/company-docs`, formData);
                // So os documentos e as pendencias: o formulario pode ter edicao nao salva.
                this.data = result.data.content;
                this.$dialog.notify.success("Documento enviado. Ele será analisado pelo Administrador Judicial.", { position: "top-right", timeout: 3000 });
            } catch (error) {
                this.notifyError(error);
            } finally {
                this.uploadingType = null;
                this.pendingType = null;
            }
        },
    },
};
</script>

<style scoped>
.back-link {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    padding: 4px 10px 4px 4px;
    border-radius: 6px;
    color: var(--v-primary-base);
    font-size: 14px;
    font-weight: 500;
    text-decoration: none;
    transition: background-color 0.15s ease;
}

.back-link:hover {
    background-color: rgba(0, 0, 0, 0.04);
}

.back-link:hover span {
    text-decoration: underline;
}
</style>
